import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ClipboardCheck, FileSpreadsheet, PackageCheck, ReceiptIndianRupee, RefreshCw, Send, ShoppingCart, FileSignature, Truck, Wallet, Hourglass, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";

const C = {
  blue: { chip: "bg-blue-50 text-blue-600", bar: "bg-gradient-to-r from-blue-500 to-blue-700", soft: "from-blue-50", btn: "bg-gradient-to-r from-blue-600 to-blue-700", ring: "bg-blue-600" },
  green: { chip: "bg-emerald-50 text-emerald-600", bar: "bg-gradient-to-r from-emerald-400 to-emerald-600", soft: "from-emerald-50", btn: "bg-gradient-to-r from-emerald-500 to-teal-600", ring: "bg-emerald-600" },
  orange: { chip: "bg-orange-50 text-orange-600", bar: "bg-gradient-to-r from-orange-400 to-orange-600", soft: "from-orange-50", btn: "bg-gradient-to-r from-orange-500 to-orange-600", ring: "bg-orange-500" },
  violet: { chip: "bg-violet-50 text-violet-600", bar: "bg-gradient-to-r from-violet-500 to-purple-600", soft: "from-violet-50", btn: "bg-gradient-to-r from-violet-500 to-purple-600", ring: "bg-violet-600" },
  rose: { chip: "bg-rose-50 text-rose-600", bar: "bg-gradient-to-r from-rose-400 to-rose-600", soft: "from-rose-50", btn: "bg-gradient-to-r from-rose-500 to-rose-600", ring: "bg-rose-500" },
};
const CARD_META = { ra: [FileSpreadsheet, "blue"], grn: [PackageCheck, "green"], rfq: [Send, "orange"], invoice: [ReceiptIndianRupee, "violet"] };
const ACT_META = { ra: [FileSpreadsheet, "blue"], award: [FileSignature, "violet"], grn: [PackageCheck, "green"], rfq: [Send, "orange"], invoice: [ReceiptIndianRupee, "rose"], po: [ShoppingCart, "blue"] };
const PEND_META = { ra: [ClipboardCheck, "orange"], grn: [Truck, "blue"], invoice: [Wallet, "rose"] };
const QUICK = [
  { to: "/qs/ra-bills", label: "RA Bills", sub: "Raise or approve bills", icon: FileSpreadsheet, c: "from-blue-600 to-blue-800" },
  { to: "/stores/grn", label: "New GRN", sub: "Receive & inspect material", icon: PackageCheck, c: "from-emerald-500 to-teal-600" },
  { to: "/purchase/rfq", label: "New RFQ", sub: "Invite vendor quotes", icon: Send, c: "from-orange-500 to-orange-600" },
  { to: "/finance/invoices", label: "Invoice Queue", sub: "3-way match & pay", icon: ReceiptIndianRupee, c: "from-violet-500 to-purple-600" },
];

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

const Card = ({ title, sub, right, children, className = "", testId }) => (
  <section data-testid={testId} className={`rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm ${className}`}>
    {title && (
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
          {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

const Hero = ({ user, d, onRefresh }) => {
  const stats = [
    [Hourglass, "My approvals", d.my_queue.length, "awaiting your action"],
    [ShoppingCart, "Open PO value", inr(Math.round(d.po_value)), `${d.po_open} open POs`],
    [Wallet, "Payables pending", inr(Math.round(d.invoices.pending_value)), `${d.invoices.overdue_30} overdue > 30 days`],
    [TrendingUp, "RA bills in approval", d.ra_pending, inr(d.ra_pending_value)],
  ];
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <section data-testid="dashboard-hero" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E64B4] via-[#17549C] to-[#0B3D7A] p-5 text-white shadow-xl shadow-blue-900/20 fade-up">
      <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10" />
      <span className="pointer-events-none absolute -bottom-24 right-40 h-48 w-48 rounded-full bg-white/5" />
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 data-testid="page-title" className="font-heading text-2xl font-bold sm:text-3xl">Welcome back, {user.name.split(" ")[0]}</h1>
            <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] ring-1 ring-white/20">{today}</span>
          </div>
          <p className="mt-1 text-sm text-blue-100">P2P Ops · live status of the procure-to-pay cycle across departments</p>
        </div>
        <Button size="sm" onClick={onRefresh} className="bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25" data-testid="dashboard-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
      </div>
      <div className="relative mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map(([Icon, label, value, sub], i) => (
          <div key={label} data-testid={`hero-stat-${i}`} className="rounded-xl bg-white/10 p-3 ring-1 ring-white/15 backdrop-blur">
            <div className="flex items-center gap-2 text-[11px] text-blue-100"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/15"><Icon className="h-3.5 w-3.5" /></span>{label}</div>
            <p className="mt-2 font-heading text-xl font-bold sm:text-2xl">{value}</p>
            <p className="text-[11px] text-blue-100/80">{sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const StatCard = ({ c, i }) => {
  const [Icon, col] = CARD_META[c.key];
  return (
    <div data-testid={`kpi-card-${c.key}`} style={{ animationDelay: `${i * 50}ms` }} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md fade-up">
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${C[col].chip}`}><Icon className="h-5 w-5" /></span>
        <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.progress ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{c.progress > 0 && <TrendingUp className="h-3 w-3" />}{c.progress}%</span>
      </div>
      <p className="mt-3 font-heading text-2xl font-bold text-slate-900">{c.value}</p>
      <p className="text-xs font-semibold text-slate-700">{c.label}</p>
      <p className="text-[11px] text-slate-400">{c.sub}</p>
      <div className="mt-3 flex justify-between text-[11px] text-slate-500"><span>{c.progress_label}</span><span className="font-semibold text-slate-700">{c.progress}%</span></div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${C[col].bar}`} style={{ width: `${c.progress}%` }} /></div>
      <p className="mt-2 text-[11px] text-slate-400">{c.foot}</p>
    </div>
  );
};

const StageBars = ({ d }) => {
  const max = Math.max(1, ...d.stage_bars.map((s) => s.count));
  const tiles = [
    [Wallet, inr(d.ra_pending_value), "RA value pending", "bg-blue-50 text-blue-700"],
    [Hourglass, d.ra_pending, "Bills in approval", "bg-emerald-50 text-emerald-700"],
    [ReceiptIndianRupee, inr(Math.round(d.invoices.pending_value)), "Invoices pending", "bg-violet-50 text-violet-700"],
    [ShoppingCart, d.rfq_open, "Open RFQs", "bg-amber-50 text-amber-700"],
  ];
  return (
    <Card title="RA Bill Pipeline" sub="Bills and value waiting at each approval stage" className="lg:col-span-2" testId="chart-ra-stage">
      <div className="space-y-3">
        {d.stage_bars.map((s) => (
          <div key={s.stage}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-800">{s.stage} <span className="font-normal text-slate-400">· {s.count} bill{s.count === 1 ? "" : "s"}</span></span>
              <span className="font-semibold text-slate-900">{inr(s.value)}</span>
            </div>
            <div className="h-6 rounded-lg bg-slate-100">
              {s.count > 0 && <div className="flex h-6 items-center justify-end rounded-lg bg-gradient-to-r from-[#1E64B4] to-[#0B3D7A] pr-2 text-[10px] font-semibold text-white transition-all" style={{ width: `${Math.max(12, (100 * s.count) / max)}%` }}>{s.count}</div>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 md:grid-cols-4">
        {tiles.map(([Icon, v, l, cls]) => (
          <div key={l} className={`rounded-xl p-3 text-center ${cls}`}>
            <Icon className="mx-auto h-4 w-4" />
            <p className="mt-1 font-heading text-base font-bold text-slate-900">{v}</p>
            <p className="text-[10px] text-slate-500">{l}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Activity = ({ items }) => (
  <Card title="Live Activity" sub="Latest approvals and entries" right={<span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />Live</span>} testId="live-activity">
    <ul className="space-y-3">
      {items.map((a) => {
        const [Icon, col] = ACT_META[a.kind];
        return (
          <li key={`${a.at}-${a.what}`} className="flex gap-3" data-testid="activity-item">
            <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${C[col].chip}`}><Icon className="h-3.5 w-3.5" /></span>
            <div className="min-w-0 text-xs">
              <p className="text-slate-700"><span className="font-semibold text-slate-900">{a.who}</span> {a.what}</p>
              <p className="truncate text-[11px] text-slate-500">{a.detail}{a.amount ? <span className="ml-1 font-semibold text-emerald-600">{inr(a.amount)}</span> : null}</p>
              <p className="text-[10px] text-slate-400">{ago(a.at)}</p>
            </div>
          </li>
        );
      })}
      {!items.length && <li className="py-6 text-center text-xs text-slate-500">No activity yet</li>}
    </ul>
  </Card>
);

const QuickActions = () => (
  <Card title="Quick Actions" sub="Jump straight into the key workflows" testId="quick-actions">
    <div className="grid grid-cols-2 gap-3">
      {QUICK.map((q) => (
        <Link key={q.to} to={q.to} data-testid={`quick-${q.to.split("/")[2]}`} className={`group flex flex-col items-center justify-center rounded-xl bg-gradient-to-br ${q.c} p-4 text-center text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5`}>
          <q.icon className="h-5 w-5" />
          <p className="mt-2 text-sm font-semibold">{q.label}</p>
          <p className="text-[10px] text-white/80">{q.sub}</p>
        </Link>
      ))}
    </div>
  </Card>
);

const RANK = ["from-amber-400 to-orange-500", "from-slate-300 to-slate-500", "from-orange-400 to-rose-500", "from-blue-400 to-blue-600", "from-emerald-400 to-teal-600"];

const TopVendors = ({ items }) => (
  <Card title="Top Vendors" sub="Ranked by purchase order value" right={<Link to="/purchase/vendors" className="flex items-center gap-1 text-xs font-medium text-sky-700" data-testid="top-vendors-view-all">View all<ArrowRight className="h-3 w-3" /></Link>} testId="top-vendors">
    <ul className="space-y-2">
      {items.map((v, i) => (
        <li key={v.vendor} className="flex items-center gap-3 rounded-xl bg-slate-50 p-2.5" data-testid={`top-vendor-${i}`}>
          <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${RANK[i]} text-xs font-bold text-white`}>
            {v.vendor.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-slate-700 shadow">{i + 1}</span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-slate-900">{v.vendor}</p>
            <p className="text-[11px] text-slate-500">{v.orders} PO{v.orders === 1 ? "" : "s"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold text-slate-900">{inr(Math.round(v.value))}</p>
            <p className="text-[10px] text-slate-400">PO value</p>
          </div>
        </li>
      ))}
      {!items.length && <li className="py-6 text-center text-xs text-slate-500">No purchase orders yet</li>}
    </ul>
  </Card>
);

const Pending = ({ items }) => (
  <Card title="Pending Actions" sub="Items requiring attention across the cycle"
    right={<span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-600">{items.reduce((s, p) => s + p.count, 0)} pending</span>} testId="pending-actions">
    <div className="grid gap-3 md:grid-cols-3">
      {items.map((p) => {
        const [Icon, col] = PEND_META[p.key];
        return (
          <div key={p.key} data-testid={`pending-${p.key}`} className={`rounded-xl border border-slate-100 bg-gradient-to-br ${C[col].soft} to-white p-4`}>
            <div className="flex items-start justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${C[col].ring} text-white`}><Icon className="h-4 w-4" /></span>
              <span className={`flex h-7 min-w-7 items-center justify-center rounded-full ${C[col].ring} px-2 text-xs font-bold text-white`}>{p.count}</span>
            </div>
            <p className="mt-3 text-sm font-bold text-slate-900">{p.label}</p>
            <p className="text-[11px] text-slate-500">{p.desc}</p>
            <Link to={p.link} className={`mt-3 flex items-center justify-center gap-1 rounded-lg ${C[col].btn} py-2 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90`} data-testid={`pending-review-${p.key}`}>Review now<ArrowRight className="h-3 w-3" /></Link>
          </div>
        );
      })}
    </div>
  </Card>
);

const Queue = ({ items }) => (
  <Card title="My Approval Queue" sub="Waiting at your workflow stage" testId="my-queue" className="lg:col-span-2">
    <ul className="divide-y divide-slate-100">
      {items.map((q) => (
        <li key={q.ref}>
          <Link to={q.link} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-sky-50" data-testid={`queue-item-${q.ref}`}>
            <span className="w-20 font-mono text-xs font-medium text-sky-700">{q.ref}</span>
            <span className="min-w-0 flex-1 truncate text-xs">{q.title}</span>
            <StatusBadge value={q.stage} className="hidden sm:inline-flex" />
            <span className={`w-10 text-right text-xs ${q.days > 5 ? "font-semibold text-rose-600" : "text-slate-500"}`}>{q.days}d</span>
          </Link>
        </li>
      ))}
      {!items.length && <li className="py-6 text-center text-xs text-slate-500">Nothing awaiting your action</li>}
    </ul>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setErr(false);
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
  }, [tick]);

  if (err) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="dashboard-error">
        Could not load the dashboard. <button className="font-semibold underline" onClick={() => setTick((t) => t + 1)} data-testid="dashboard-retry">Try again</button>
      </div>
    );
  }
  if (!d) return <div className="text-sm text-slate-500">Loading...</div>;
  return (
    <div className="space-y-4">
      <Hero user={user} d={d} onRefresh={() => setTick((t) => t + 1)} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{d.cards.map((c, i) => <StatCard key={c.key} c={c} i={i} />)}</div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StageBars d={d} />
        <Activity items={d.activity} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Queue items={d.my_queue} />
        <QuickActions />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2"><Pending items={d.pending} /></div>
        <TopVendors items={d.top_vendors} />
      </div>
    </div>
  );
}
